import React from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  ProspectAnalyticsPage,
  ProspectAnalyticsRow,
  ProspectSortColumn,
} from "../../../../types/onboardingAnalytics";
import { LeadSource, OnboardingState, SignMethod } from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";
import {
  LEAD_SOURCE_LABELS,
  leadSourceLabel,
  signMethodLabel,
} from "../../../../constants/contractSigning";
import {
  EMPTY_CELL,
  formatCount,
  humaniseSegment,
  orEmpty,
} from "../../../../utils/analyticsFormat";
import { setupFeeStatusLabel } from "../../../../utils/setupFee";

interface ProspectAnalyticsTableProps {
  page: ProspectAnalyticsPage;
  rows: number;
  first: number;
  sortField: ProspectSortColumn;
  sortOrder: 1 | -1;
  onPage: (event: DataTableStateEvent) => void;
  onSort: (event: DataTableStateEvent) => void;
  onExport: () => void;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return EMPTY_CELL;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? EMPTY_CELL : date.toLocaleDateString();
};

/**
 * The filterable prospect table.
 *
 * Paging, filtering and sorting all happen on the server, which is why this is a
 * separate component from the operational prospect list rather than a reuse of
 * it: that one fetches everything and filters in the browser, and its rows carry
 * actions. The two share nothing but a prospect's name.
 *
 * Only the eleven columns the API will sort by are marked sortable. Offering any
 * other column would produce a control that answers 400 (FR-013).
 */
const ProspectAnalyticsTable: React.FC<ProspectAnalyticsTableProps> = ({
  page,
  rows,
  first,
  sortField,
  sortOrder,
  onPage,
  onSort,
  onExport,
}) => {
  const navigate = useNavigate();

  const stageBody = (row: ProspectAnalyticsRow) =>
    stateLabel(row.state as OnboardingState);

  const typeBody = (row: ProspectAnalyticsRow) =>
    humaniseSegment(String(row.customerType));

  // The four nullable columns. Each renders the repo's em-dash rather than
  // "null", "undefined" or a value that reads like data (FR-016).
  const leadSourceBody = (row: ProspectAnalyticsRow) => {
    if (!row.leadSource) return EMPTY_CELL;
    return String(row.leadSource) in LEAD_SOURCE_LABELS
      ? leadSourceLabel(row.leadSource as LeadSource)
      : humaniseSegment(String(row.leadSource));
  };

  const signMethodBody = (row: ProspectAnalyticsRow) =>
    row.signMethod ? signMethodLabel(row.signMethod as SignMethod) : EMPTY_CELL;

  const lastActorBody = (row: ProspectAnalyticsRow) => orEmpty(row.lastActor);

  const lastChangedBody = (row: ProspectAnalyticsRow) =>
    formatDate(row.lastChangedAt);

  return (
    <>
      <div className="flex justify-content-end mb-2">
        <Button
          label="Export loaded rows"
          icon="pi pi-download"
          size="small"
          outlined
          onClick={onExport}
          disabled={!page.prospects.length}
          tooltip="Exports the rows currently loaded, for the filters in use"
          tooltipOptions={{ position: "left" }}
        />
      </div>

      <DataTable
        value={page.prospects}
        dataKey="id"
        lazy
        paginator
        rows={rows}
        first={first}
        totalRecords={page.total}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        removableSort
        selectionMode="single"
        onSelectionChange={(e) =>
          navigate(
            `/onboarding/prospects/${(e.value as ProspectAnalyticsRow).id}`
          )
        }
        className="p-datatable-sm"
        emptyMessage="No prospects match these filters."
        aria-label="Prospects"
      >
        <Column field="fullName" header="Name" sortField="name" sortable />
        <Column header="Type" body={typeBody} sortField="customer_type" sortable />
        <Column header="Stage" body={stageBody} sortField="state" sortable />
        <Column
          header="Lead source"
          body={leadSourceBody}
          sortField="lead_source"
          sortable
        />
        <Column field="vendor" header="Vendor" sortField="vendor" sortable />
        <Column
          header="Sign method"
          body={signMethodBody}
          sortField="sign_method"
          sortable
        />
        <Column
          header="Created"
          body={(r: ProspectAnalyticsRow) => formatDate(r.createdAt)}
          sortField="created_at"
          sortable
        />
        <Column
          header="Last changed"
          body={lastChangedBody}
          sortField="last_changed_at"
          sortable
        />
        <Column
          header="Days in stage"
          body={(r: ProspectAnalyticsRow) => formatCount(r.daysInState)}
          sortField="days_in_state"
          sortable
        />
        <Column
          header="Days total"
          body={(r: ProspectAnalyticsRow) => formatCount(r.daysTotal)}
          sortField="days_total"
          sortable
        />
        <Column
          header="Last actor"
          body={lastActorBody}
          sortField="last_actor"
          sortable
        />
        {/* Not sortable: the API's sort whitelist does not include it. */}
        <Column
          header="Setup fee"
          body={(r: ProspectAnalyticsRow) =>
            r.setupFeeStatus ? setupFeeStatusLabel(r.setupFeeStatus) : EMPTY_CELL
          }
        />
      </DataTable>
    </>
  );
};

export default ProspectAnalyticsTable;
